import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useRouteSeo } from "../seo/useRouteSeo";

/**
 * SocialMeta — client-side replacement for <Helmet>. We swap the existing
 * <meta property="og:..." /> tags out for per-page values on mount, and
 * restore the defaults on unmount.
 *
 * Important honest note: most crawlers (Twitter / X, Facebook, iMessage,
 * Slack, WhatsApp) do NOT execute JavaScript and will see only the
 * default tags from public/index.html. LinkedIn and Discord DO render JS
 * for previews in some cases, so this is still worth doing — and the URL
 * remains correct + shareable regardless of which crawler picks it up.
 *
 * Google's crawler DOES execute JavaScript, so `canonical` + `description`
 * + JSON-LD injected here will be picked up for SEO indexing.
 */
export function SocialMeta(props) {
  const { pathname } = useLocation();
  // Routes in src/seo/routes.js are the single source of truth so the live DOM
  // always matches the pre-rendered HTML; props only fill gaps (e.g. jsonLd).
  const seo = useRouteSeo(pathname);
  const title = seo?.title ?? props.title;
  const description = seo?.description ?? props.description;
  const image = seo?.image ?? props.image;
  const canonical = seo?.canonical ?? props.canonical;
  const url = seo?.canonical ?? props.url;
  const { jsonLd } = props;
  const noindex = seo ? !!seo.noindex : props.noindex;
  useEffect(() => {
    const previous = {};
    const set = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        // Create the tag if missing
        el = document.createElement("meta");
        const [k, v] = selector.replace("meta[", "").replace("]", "").split("=");
        el.setAttribute(k.trim(), v.replace(/['"]/g, ""));
        document.head.appendChild(el);
      }
      previous[selector] = el.getAttribute(attr);
      if (value != null) el.setAttribute(attr, value);
    };

    if (title) {
      previous.__title = document.title;
      document.title = title;
      set('meta[property="og:title"]', "content", title);
      set('meta[name="twitter:title"]', "content", title);
    }
    if (description) {
      set('meta[name="description"]', "content", description);
      set('meta[property="og:description"]', "content", description);
      set('meta[name="twitter:description"]', "content", description);
    }
    if (image) {
      set('meta[property="og:image"]', "content", image);
      set('meta[name="twitter:image"]', "content", image);
    }
    if (url) {
      set('meta[property="og:url"]', "content", url);
    }
    if (noindex) {
      set('meta[name="robots"]', "content", "noindex, nofollow");
    }

    // Canonical URL — critical for SEO. If a canonical link already exists we
    // swap its href for the current page and restore on unmount.
    let canonicalEl = null;
    let previousCanonicalHref = null;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      } else {
        previousCanonicalHref = canonicalEl.getAttribute("href");
      }
      canonicalEl.setAttribute("href", canonical);
    }

    // JSON-LD structured data — inject a script tag scoped by data-attr so we
    // can cleanly remove it on unmount without touching any other JSON-LD.
    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.setAttribute("data-social-meta", "true");
      jsonLdEl.textContent = typeof jsonLd === "string" ? jsonLd : JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      if (previous.__title) document.title = previous.__title;
      Object.keys(previous).forEach((k) => {
        if (k === "__title") return;
        const el = document.head.querySelector(k);
        if (el && previous[k] != null) el.setAttribute("content", previous[k]);
      });
      if (canonicalEl) {
        if (previousCanonicalHref != null) {
          canonicalEl.setAttribute("href", previousCanonicalHref);
        } else {
          canonicalEl.remove();
        }
      }
      if (jsonLdEl && jsonLdEl.parentNode) {
        jsonLdEl.parentNode.removeChild(jsonLdEl);
      }
    };
  }, [title, description, image, url, canonical, jsonLd, noindex]);

  return null;
}
